/**
 * Challenge join URLs embed the lobby PeerJS id. The host board opens in another
 * tab with a different id, so joiners often land on the lobby tab's data channel.
 * This proxy answers game handshake traffic from the lobby using IndexedDB state.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useMessages } from 'dignity.js/react';
import { preferGameState } from '../chess/game.js';
import { listGames, loadGame, saveGame } from '../chess/gameStore.js';
import {
  applySignedGameAction,
  createSignedGameSnapshot,
  verifySignedGameSnapshot
} from '../chess/protocol.js';
import { loadPendingOutgoingChallenge } from './pendingOutgoingChallenge.js';
import {
  publishLocalGameUpdate,
  publishLocalGameWire,
  subscribeLocalGameUpdate,
  subscribeLocalGameWire
} from './gameLocalBus.js';
import { LOBBY_GAME_WIRE_TYPES } from './lobbyGameWire.js';
import { lobbyDebug } from './lobbyDebug.js';

export { LOBBY_GAME_WIRE_TYPES } from './lobbyGameWire.js';

function wireSignedSnapshot(game, identity) {
  return createSignedGameSnapshot(JSON.parse(JSON.stringify(game)), identity);
}

const CERTIFICATE_WIRE_TYPES = LOBBY_GAME_WIRE_TYPES;
const ACTIVE_GAME_STATUSES = new Set(['waiting', 'starting', 'playing']);

function isSeatedPlayer(game, identity) {
  const playerId = identity?.playerId;
  if (!playerId || !game) {
    return false;
  }
  return game.players?.white?.playerId === playerId
    || game.players?.black?.playerId === playerId;
}

async function loadOwnedWaitingGame(gameId, identity) {
  const game = await loadGame(gameId);
  if (!game || !isSeatedPlayer(game, identity)) {
    return null;
  }
  return game;
}

async function listOwnedActiveGames(identity) {
  const games = await listGames();
  return games.filter(
    (game) =>
      isSeatedPlayer(game, identity)
      && ACTIVE_GAME_STATUSES.has(game.status)
  );
}

export default function useLobbyGameProxy({
  node,
  identity,
  pendingOutgoingChallenge = null,
  openPeerIds = []
}) {
  const processedRef = useRef(0);
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const pendingRef = useRef(pendingOutgoingChallenge);
  pendingRef.current = pendingOutgoingChallenge;
  const pendingWireRef = useRef(new Map());

  const gameMessageFilter = useCallback(
    (message) => Boolean(
      message?.type?.startsWith('dechess-game-')
      && message?.payload?.gameId
    ),
    []
  );
  const messages = useMessages(node, gameMessageFilter);

  const sendSnapshot = useCallback(async (game, peerIds = []) => {
    if (!node || !game?.id) {
      return;
    }
    const targets = [...new Set((peerIds || []).filter(Boolean))];
    const snapshot = wireSignedSnapshot(game, identityRef.current);
    await node.broadcastMessage(
      'dechess-game-state',
      { gameId: game.id, snapshot },
      {
        broadcastScope: `game:${game.id}`,
        connectToPeers: targets,
        fanoutPeerIds: targets.length ? targets : undefined
      }
    );
    publishLocalGameUpdate(game, { source: 'lobby-proxy-push' });
    lobbyDebug('lobby-game-proxy-push', {
      gameId: game.id,
      revision: game.revision,
      status: game.status,
      startReady: game.startReady,
      targets
    });
  }, [node]);

  const fanoutWireEnvelope = useCallback((envelope, peerIds = []) => {
    if (!node || !envelope?.gameId || !envelope?.messageType) {
      return;
    }
    const targets = [...new Set(
      (peerIds || []).filter(
        (peerId) => peerId && peerId !== identityRef.current.peerId
      )
    )];
    if (!targets.length) {
      return;
    }
    void node.broadcastMessage(
      envelope.messageType,
      { gameId: envelope.gameId, ...envelope.payload },
      {
        broadcastScope: `game:${envelope.gameId}`,
        connectToPeers: targets,
        fanoutPeerIds: targets
      }
    ).then(() => {
      lobbyDebug('lobby-game-proxy-wire-fanout', {
        gameId: envelope.gameId,
        messageType: envelope.messageType,
        targets
      });
    }).catch((error) => {
      lobbyDebug('lobby-game-proxy-wire-fanout-fail', {
        gameId: envelope.gameId,
        message: error?.message || String(error)
      });
    });
  }, [node]);

  const rememberWireEnvelope = useCallback((envelope) => {
    if (!envelope?.gameId || !CERTIFICATE_WIRE_TYPES.has(envelope.messageType)) {
      return;
    }
    const key = `${envelope.gameId}:${envelope.messageType}:${
      envelope.payload?.proof?.playerId
      || envelope.payload?.action?.playerId
      || envelope.payload?.action?.actionId
      || envelope.payload?.signerId
      || envelope.payload?.certificate?.certificateHash
      || envelope.payload?.snapshot?.signerId
      || 'default'
    }`;
    pendingWireRef.current.set(key, envelope);
  }, []);

  // Proactively push pending / in-progress owned games to linked joiners.
  useEffect(() => {
    if (!node) {
      return undefined;
    }
    const pushPending = async () => {
      const targets = (openPeerIds || []).filter(
        (peerId) => peerId && peerId !== identityRef.current.peerId
      );
      if (!targets.length) {
        return;
      }
      const pending = pendingRef.current || loadPendingOutgoingChallenge();
      const pendingGameId = pending?.challenge?.gameId;
      const owned = await listOwnedActiveGames(identityRef.current);
      const byId = new Map(owned.map((game) => [game.id, game]));
      if (pendingGameId && !byId.has(pendingGameId)) {
        const pendingGame = await loadOwnedWaitingGame(
          pendingGameId,
          identityRef.current
        );
        if (pendingGame) {
          byId.set(pendingGame.id, pendingGame);
        }
      }
      for (const game of byId.values()) {
        try {
          await sendSnapshot(game, targets);
        } catch (error) {
          lobbyDebug('lobby-game-proxy-push-fail', {
            gameId: game.id,
            message: error?.message || String(error)
          });
        }
      }
    };
    void pushPending();
    const timer = window.setInterval(() => {
      void pushPending();
    }, 1000);
    return () => window.clearInterval(timer);
  }, [node, openPeerIds, pendingOutgoingChallenge, sendSnapshot]);

  // Host board tab publishes actions here; fan them out to the joiner on the lobby peer.
  useEffect(() => {
    if (!node) {
      return undefined;
    }
    return subscribeLocalGameUpdate((message) => {
      if (!message?.game?.id) {
        return;
      }
      if (
        message.source === 'lobby-proxy-push'
        || message.source === 'lobby-proxy-action'
        || message.source === 'lobby-proxy-state'
      ) {
        return;
      }
      if (!isSeatedPlayer(message.game, identityRef.current)) {
        return;
      }
      const targets = (openPeerIds || []).filter(
        (peerId) => peerId && peerId !== identityRef.current.peerId
      );
      if (!targets.length) {
        return;
      }
      void (async () => {
        const stored = await loadGame(message.game.id);
        const next = preferGameState(stored, message.game);
        await saveGame(next);
        await sendSnapshot(next, targets);
      })().catch((error) => {
        lobbyDebug('lobby-game-proxy-board-relay-fail', {
          gameId: message.game.id,
          message: error?.message || String(error)
        });
      });
    });
  }, [node, openPeerIds, sendSnapshot]);

  // Board tab certificate / VDF envelopes → joiner over the lobby peer link.
  useEffect(() => {
    if (!node) {
      return undefined;
    }
    return subscribeLocalGameWire((envelope) => {
      if (envelope.source === 'lobby-proxy-wire') {
        return;
      }
      if (!CERTIFICATE_WIRE_TYPES.has(envelope.messageType)) {
        return;
      }
      rememberWireEnvelope(envelope);
      fanoutWireEnvelope(envelope, openPeerIds);
    });
  }, [fanoutWireEnvelope, node, openPeerIds, rememberWireEnvelope]);

  // Re-fanout cached VDF/cert envelopes when the joiner link appears later.
  useEffect(() => {
    if (!node || !(openPeerIds || []).length) {
      return undefined;
    }
    const targets = (openPeerIds || []).filter(
      (peerId) => peerId && peerId !== identityRef.current.peerId
    );
    if (!targets.length) {
      return undefined;
    }
    for (const envelope of pendingWireRef.current.values()) {
      fanoutWireEnvelope(envelope, targets);
    }
    const timer = window.setInterval(() => {
      for (const envelope of pendingWireRef.current.values()) {
        fanoutWireEnvelope(envelope, targets);
      }
    }, 4000);
    return () => window.clearInterval(timer);
  }, [fanoutWireEnvelope, node, openPeerIds]);

  useEffect(() => {
    if (!node) {
      processedRef.current = 0;
      return;
    }
    if (messages.length < processedRef.current) {
      processedRef.current = 0;
    }
    if (processedRef.current >= messages.length) {
      return;
    }
    const pending = messages.slice(processedRef.current);
    processedRef.current = messages.length;

    const process = async () => {
      for (const message of pending) {
        const gameId = message.payload?.gameId;
        if (!gameId) {
          continue;
        }
        try {
          if (message.type === 'dechess-game-state-request') {
            const game = await loadOwnedWaitingGame(gameId, identityRef.current);
            if (!game) {
              continue;
            }
            await sendSnapshot(game, [message.senderId]);
            continue;
          }

          if (message.type === 'dechess-game-action') {
            const action = message.payload?.action;
            if (!action || action.gameId !== gameId) {
              continue;
            }
            const current = await loadOwnedWaitingGame(gameId, identityRef.current);
            if (!current) {
              continue;
            }
            const next = preferGameState(current, applySignedGameAction(current, action));
            await saveGame(next);
            publishLocalGameUpdate(next, { source: 'lobby-proxy-action' });
            const fanout = [...new Set(
              [message.senderId, ...(openPeerIds || [])]
                .filter((peerId) => peerId && peerId !== identityRef.current.peerId)
            )];
            await sendSnapshot(next, fanout);
            lobbyDebug('lobby-game-proxy-action', {
              gameId,
              type: action.type,
              revision: next.revision,
              senderId: message.senderId,
              fanout
            });
            continue;
          }

          if (message.type === 'dechess-game-state') {
            const signed = message.payload?.snapshot;
            if (!verifySignedGameSnapshot(signed)) {
              continue;
            }
            const snapshot = signed.game;
            if (!snapshot || snapshot.id !== gameId) {
              continue;
            }
            if (!isSeatedPlayer(snapshot, identityRef.current)) {
              continue;
            }
            const current = await loadGame(gameId);
            const next = preferGameState(current, snapshot);
            if (!next) {
              continue;
            }
            await saveGame(next);
            publishLocalGameUpdate(next, { source: 'lobby-proxy-state' });
            continue;
          }

          if (CERTIFICATE_WIRE_TYPES.has(message.type)) {
            const envelope = {
              gameId,
              messageType: message.type,
              payload: message.payload || {},
              senderId: message.senderId || '',
              source: 'lobby-proxy-wire'
            };
            // Cache joiner→host cert/VDF so a late host-board subscribe still
            // sees retries via the board share interval + local wire path.
            if (
              message.type === 'dechess-game-vdf-proof'
              || message.type === 'dechess-game-certificate-final'
              || message.type === 'dechess-game-certificate-proposal'
            ) {
              rememberWireEnvelope({
                ...envelope,
                source: 'board'
              });
            }
            publishLocalGameWire(envelope);
          }
        } catch (error) {
          lobbyDebug('lobby-game-proxy-error', {
            gameId,
            type: message.type,
            message: error?.message || String(error)
          });
        }
      }
    };

    void process();
  }, [fanoutWireEnvelope, messages, node, openPeerIds, rememberWireEnvelope, sendSnapshot]);
}
